import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, Download, Eye, Trash2, RefreshCw, Loader2, 
  Search, Plus, FileUp, CheckCircle2, AlertCircle, User, SkipForward
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { formatDateDisplay, truncateName } from '../utils/dateUtils';
import { formatErrorMessage } from '../utils/formatError';

const API = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL + '/api' : '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const CreateDocuments = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showViewDocsDialog, setShowViewDocsDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [skipMissingFields, setSkipMissingFields] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [candidatesRes, templatesRes, docsRes] = await Promise.all([
        axios.get(`${API}/document-templates/candidates`, { headers: getAuthHeader() }),
        axios.get(`${API}/document-templates/templates`, { headers: getAuthHeader() }),
        axios.get(`${API}/document-templates/generated`, { headers: getAuthHeader() })
      ]);
      
      setCandidates(candidatesRes.data.candidates || []);
      // Filter templates to show only TAP-targeted templates (target is 'tap' or undefined/null for backward compatibility)
      const allTemplates = templatesRes.data.templates || [];
      const tapTemplates = allTemplates.filter(t => !t.target || t.target === 'tap');
      setTemplates(tapTemplates);
      setGeneratedDocs(docsRes.data.documents || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidateDetails = async (candidateId) => {
    try {
      setLoadingDetails(true);
      const response = await axios.get(`${API}/document-templates/candidates/${candidateId}`, { headers: getAuthHeader() });
      setCandidateDetails(response.data);
    } catch (error) {
      console.error('Error fetching candidate details:', error);
      toast.error('Failed to load candidate details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelectCandidate = async (candidate) => {
    setSelectedCandidate(candidate);
    await fetchCandidateDetails(candidate.id);
    setShowGenerateDialog(true);
  };

  const handleGenerateDocument = async () => {
    if (!selectedCandidate || !selectedTemplate) {
      toast.error('Please select a document template');
      return;
    }

    // Check required fields (only if not skipping)
    const requiredFields = selectedTemplate.required_fields || [];
    const missingRequired = requiredFields.filter(
      f => !candidateDetails?.candidate?.[f]
    );

    if (missingRequired.length > 0 && !skipMissingFields) {
      toast.error(`Missing required fields: ${missingRequired.join(', ')}. Enable "Skip Missing Fields" to proceed anyway.`);
      return;
    }

    try {
      setGenerating(true);
      
      const formData = new FormData();
      formData.append('template_id', selectedTemplate.id);
      formData.append('candidate_id', selectedCandidate.id);
      formData.append('additional_data', JSON.stringify({}));
      formData.append('skip_missing_fields', skipMissingFields.toString());

      const response = await axios.post(`${API}/document-templates/generate`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document generated successfully!');
      setShowGenerateDialog(false);
      setSelectedCandidate(null);
      setSelectedTemplate(null);
      setCandidateDetails(null);
      setSkipMissingFields(false);
      fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error(formatErrorMessage(error, 'Failed to generate document'));
    } finally {
      setGenerating(false);
    }
  };

  const handleViewCandidateDocs = async (candidate) => {
    setSelectedCandidate(candidate);
    const candidateDocs = generatedDocs.filter(d => d.candidate_id === candidate.id);
    setShowViewDocsDialog(true);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Prefer proxying through our backend when we have a OneDrive file_id —
      // that way any app-authorized user gets the file without needing their
      // own Microsoft account to be on the shared link's access list. This
      // fixes the "You can't access this item" error on the Microsoft 365
      // permission page that raw shareable_link redirects were showing.
      if (doc.onedrive_file_id) {
        const proxyRes = await axios.get(
          `${API}/onedrive/proxy/${doc.onedrive_file_id}`,
          {
            headers: getAuthHeader(),
            params: { filename: doc.filename || 'document.pdf', download: true },
            responseType: 'blob',
          }
        );
        const url = window.URL.createObjectURL(proxyRes.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.filename || 'document.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      }
      // No file_id but we do have a legacy shareable URL — best-effort fallback
      if (doc.onedrive_url) {
        window.open(doc.onedrive_url, '_blank');
        return;
      }

      // Fallback to local download
      try {
        const response = await axios.get(`${API}/document-templates/generated/${doc.id}/download`, {
          headers: getAuthHeader(),
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.filename || 'document.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (downloadError) {
        // If blob download fails, try redirect
        if (downloadError.response?.status === 307 || downloadError.response?.data?.redirect_url) {
          window.open(downloadError.response.data.redirect_url, '_blank');
        } else {
          throw downloadError;
        }
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error(formatErrorMessage(error, 'Failed to download document. Please regenerate it.'));
    }
  };

  const handleViewDocument = async (doc) => {
    try {
      // Same proxy strategy as download but open inline in a new tab.
      if (doc.onedrive_file_id) {
        const proxyRes = await axios.get(
          `${API}/onedrive/proxy/${doc.onedrive_file_id}`,
          {
            headers: getAuthHeader(),
            params: { filename: doc.filename || 'document.pdf', download: false },
            responseType: 'blob',
          }
        );
        const blobUrl = window.URL.createObjectURL(proxyRes.data);
        window.open(blobUrl, '_blank');
        // Revoke after a delay so the new tab has time to load the blob
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
        return;
      }
      // Legacy fallback to raw shareable URL
      if (doc.onedrive_url) {
        window.open(doc.onedrive_url, '_blank');
        return;
      }
      
      // Try to get view URL from backend
      try {
        const response = await axios.get(`${API}/document-templates/generated/${doc.id}/view`, {
          headers: getAuthHeader()
        });
        
        if (response.data?.view_url) {
          window.open(response.data.view_url, '_blank');
        } else {
          // Open direct view endpoint in new tab
          window.open(`${API}/document-templates/generated/${doc.id}/view`, '_blank');
        }
      } catch (viewError) {
        toast.error(viewError.response?.data?.detail || 'Failed to view document. Please regenerate it.');
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Failed to view document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API}/document-templates/generated/${docId}`, { headers: getAuthHeader() });
      toast.success('Document deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const filteredCandidates = candidates.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.passport_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.emirates_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCandidateDocCount = (candidateId) => {
    return generatedDocs.filter(d => d.candidate_id === candidateId).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Create Documents</h2>
          <p className="text-sm text-slate-500">Generate standard documents for TAP candidates</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by name, passport, or Emirates ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Available Templates */}
      {templates.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Available Document Templates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {templates.map(t => (
                <Badge key={t.id} variant="outline" className="gap-1">
                  <FileText className="w-3 h-3" />
                  {t.name}
                  {t.is_bilingual && <span className="text-xs">(EN/AR)</span>}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Candidates List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Candidates ({filteredCandidates.length})</CardTitle>
          <CardDescription>Select a candidate to create documents</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Passport</TableHead>
                  <TableHead>Nationality</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Documents</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCandidates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No candidates found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCandidates.map(candidate => (
                    <TableRow key={candidate.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          {truncateName(candidate.name, 3)}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{candidate.passport_number || '-'}</TableCell>
                      <TableCell>{candidate.nationality || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={candidate.status === 'employee' ? 'default' : 'secondary'}>
                          {candidate.status || 'pending'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline" 
                          className={getCandidateDocCount(candidate.id) > 0 ? 'text-green-600 border-green-300' : ''}
                        >
                          {getCandidateDocCount(candidate.id)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-2">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleSelectCandidate(candidate)}
                            className="gap-1"
                            disabled={templates.length === 0}
                          >
                            <Plus className="w-3 h-3" />
                            Create
                          </Button>
                          {getCandidateDocCount(candidate.id) > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewCandidateDocs(candidate)}
                              className="gap-1"
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Generate Document Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate Document for {selectedCandidate?.name}</DialogTitle>
            <DialogDescription>
              Select a document template and verify candidate information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Template Selection */}
            <div className="space-y-2">
              <Label>Select Document Template *</Label>
              <Select
                value={selectedTemplate?.id || ''}
                onValueChange={(value) => setSelectedTemplate(templates.find(t => t.id === value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} {t.is_bilingual && '(EN/AR)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Required Fields Info */}
            {selectedTemplate && selectedTemplate.required_fields?.length > 0 && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Required Fields for this template:
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedTemplate.required_fields.map(f => (
                    <Badge 
                      key={f} 
                      variant={candidateDetails?.candidate?.[f] ? 'default' : 'destructive'}
                      className="text-xs"
                    >
                      {f}
                      {candidateDetails?.candidate?.[f] ? (
                        <CheckCircle2 className="w-3 h-3 ml-1" />
                      ) : (
                        <AlertCircle className="w-3 h-3 ml-1" />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Candidate Details Preview */}
            {loadingDetails ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : candidateDetails?.candidate && (
              <div className="space-y-2">
                <Label>Candidate Information</Label>
                <div className="grid grid-cols-2 gap-2 text-sm p-3 bg-slate-50 rounded-lg">
                  <div><span className="text-slate-500">Name:</span> {candidateDetails.candidate.name || '-'}</div>
                  <div><span className="text-slate-500">Passport:</span> {candidateDetails.candidate.passport_number || '-'}</div>
                  <div><span className="text-slate-500">Emirates ID:</span> {candidateDetails.candidate.emirates_id || '-'}</div>
                  <div><span className="text-slate-500">Nationality:</span> {candidateDetails.candidate.nationality || '-'}</div>
                  <div><span className="text-slate-500">Phone:</span> {candidateDetails.candidate.phone || '-'}</div>
                  <div><span className="text-slate-500">Position:</span> {candidateDetails.candidate.position || '-'}</div>
                </div>

                {candidateDetails.missing_fields?.length > 0 && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Missing fields: {candidateDetails.missing_fields.join(', ')}
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      Please update candidate information in TAP before generating documents, or enable "Skip Missing Fields" below.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Skip Missing Fields Option */}
            {selectedTemplate && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                <Checkbox
                  id="skip-missing"
                  checked={skipMissingFields}
                  onCheckedChange={(checked) => setSkipMissingFields(checked)}
                />
                <div className="flex-1">
                  <Label htmlFor="skip-missing" className="cursor-pointer flex items-center gap-2">
                    <SkipForward className="w-4 h-4 text-amber-600" />
                    Skip Missing Fields
                  </Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Generate document even if some candidate fields are empty. Empty fields will be left blank in the document.
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateDocument} 
              disabled={!selectedTemplate || generating}
              className="gap-2"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileUp className="w-4 h-4" />
                  Generate Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Documents Dialog */}
      <Dialog open={showViewDocsDialog} onOpenChange={setShowViewDocsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Documents for {selectedCandidate?.name}</DialogTitle>
            <DialogDescription>
              View and download generated documents
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {generatedDocs
              .filter(d => d.candidate_id === selectedCandidate?.id)
              .map(doc => (
                <div 
                  key={doc.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-red-500" />
                    <div>
                      <p className="font-medium text-sm">{doc.template_name}</p>
                      <p className="text-xs text-slate-500">
                        Generated: {formatDateDisplay(doc.generated_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewDocument(doc)}
                      className="gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadDocument(doc)}
                      className="gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteDocument(doc.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}

            {generatedDocs.filter(d => d.candidate_id === selectedCandidate?.id).length === 0 && (
              <p className="text-center text-slate-500 py-4">No documents found</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDocsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateDocuments;
